import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import WaitlistApiService from '../../api/WaitlistApiService';
import authenticationManager from '../../Auth/AuthenticationManager';

const PENDING_STATUS = 1;
const ACCEPTED_STATUS = 2;
const DECLINED_STATUS = 3;
const EXPIRED_STATUS = 4;
const RETRACTED_STATUS = 5;

function statusLabel(status) {
    switch (status) {
    case PENDING_STATUS: return 'Pending your response';
    case ACCEPTED_STATUS: return 'Accepted';
    case DECLINED_STATUS: return 'Declined';
    case EXPIRED_STATUS: return 'Expired';
    case RETRACTED_STATUS: return 'No longer available';
    default: return 'Unknown';
    }
}

function formatDate(value) {
    if (!value) return '';
    return new Date(value).toLocaleDateString();
}

function formatDateTime(value) {
    if (!value) return '';
    return new Date(value).toLocaleString();
}

export default function WaitlistOfferPage(props) {
    const { setSnackbarProperties } = props;
    const { token } = useParams();
    const navigate = useNavigate();

    const [offer, setOffer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const waitlistApiService = new WaitlistApiService();

    useEffect(() => {
        document.title = 'Executive Storage Suites - Storage Offer';

        if (!authenticationManager.isAuthorized) {
            navigate(`/login?returnUrl=/waitlist/offer/${token}`);
            return;
        }

        async function load() {
            setLoading(true);
            const response = await waitlistApiService.getOffer(token);
            if (response?.success) {
                setOffer(response.data);
            } else {
                setSnackbarProperties(response?.message || 'Unable to load offer', 'error');
            }
            setLoading(false);
        }
        load();
    }, [token]);

    async function handleAccept() {
        setSubmitting(true);
        const response = await waitlistApiService.acceptOffer(token);
        setSubmitting(false);

        if (response?.success) {
            setSnackbarProperties('Offer accepted — your unit is reserved', 'success');
            setOffer(response.data);
        } else {
            setSnackbarProperties(response?.message || 'Unable to accept offer', 'error');
        }
    }

    async function handleDecline() {
        setSubmitting(true);
        const response = await waitlistApiService.declineOffer(token);
        setSubmitting(false);

        if (response?.success) {
            setSnackbarProperties('Offer declined', 'warning');
            navigate('/my-waitlist');
        } else {
            setSnackbarProperties(response?.message || 'Unable to decline offer', 'error');
        }
    }

    if (loading) {
        return (
            <Backdrop open={true}>
                <CircularProgress color="inherit" />
            </Backdrop>
        );
    }

    if (!offer) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h5">Offer not found</Typography>
                <Typography>This offer may have expired or been declined.</Typography>
                <Button sx={{ mt: 2 }} variant="contained" onClick={() => navigate('/my-waitlist')}>Back to My Waitlist</Button>
            </Box>
        );
    }

    const isPending = offer.status === PENDING_STATUS;
    const isExpired = new Date(offer.expiresUtc) < new Date();

    return (
        <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h4" gutterBottom>Storage Unit Offer</Typography>
                    <Stack spacing={1} sx={{ mb: 2 }}>
                        <Typography><strong>Unit:</strong> {offer.storageUnitName}</Typography>
                        <Typography><strong>Size:</strong> {offer.storageSize}</Typography>
                        {offer.availableFrom && (
                            <Typography><strong>Available from:</strong> {formatDate(offer.availableFrom)}</Typography>
                        )}
                        <Typography><strong>Respond by:</strong> {formatDateTime(offer.expiresUtc)}</Typography>
                        <Typography><strong>Status:</strong> {statusLabel(offer.status)}</Typography>
                    </Stack>

                    {isPending && !isExpired && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                No payment is collected when you accept. We&apos;ll follow up with lease and payment details
                                closer to your move-in date.
                            </Typography>
                            <Stack direction="row" spacing={2}>
                                <Button variant="contained" color="success" onClick={handleAccept} disabled={submitting}>
                                    Accept Unit
                                </Button>
                                <Button variant="outlined" color="warning" onClick={handleDecline} disabled={submitting}>
                                    No Thanks
                                </Button>
                            </Stack>
                        </>
                    )}

                    {offer.status === ACCEPTED_STATUS && (
                        <Typography variant="body1" color="success.main">
                            You&apos;ve accepted this offer. We&apos;ll be in touch closer to your move-in date.
                        </Typography>
                    )}

                    {(offer.status === DECLINED_STATUS || offer.status === EXPIRED_STATUS || offer.status === RETRACTED_STATUS) && (
                        <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/my-waitlist')}>
                            Back to My Waitlist
                        </Button>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

WaitlistOfferPage.propTypes = {
    setSnackbarProperties: PropTypes.func.isRequired,
};